import {
  ADD_PLACES_ATTRIBUTE,
  ADD_CARDS_ATTRIBUTE,
  ADD_LINES_ATTRIBUTE,
  ADD_NOTES_ATTRIBUTE,
  REMOVE_PLACES_ATTRIBUTE,
  REMOVE_CARDS_ATTRIBUTE,
  REMOVE_LINES_ATTRIBUTE,
  REMOVE_NOTES_ATTRIBUTE,
  EDIT_PLACES_ATTRIBUTE,
  EDIT_CARDS_ATTRIBUTE,
  EDIT_NOTES_ATTRIBUTE,
  REORDER_CHARACTER_ATTRIBUTE,
  REORDER_PLACES_ATTRIBUTE,
  REORDER_CARDS_ATTRIBUTE,
  REORDER_NOTES_ATTRIBUTE,
  LOAD_CUSTOM_ATTRIBUTES,
  CUSTOM_ATTRIBUTE_ERROR,
} from '../constants/ActionTypes'
import * as initialState from '../store/initialState'

/**
 * @typedef {import('../index').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('../index').Attribute} Attribute
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 * @typedef {import('../types/plottr').LegacyCustomAttributes} LegacyCustomAttributes
 */

/**
 * @typedef {ReturnType<typeof _customAttributeError> | ReturnType<typeof _addPlacesAttribute> | ReturnType<typeof removePlaceAttr> | ReturnType<typeof editPlaceAttr> | ReturnType<typeof addLineAttr> | ReturnType<typeof removeLineAttr> | ReturnType<typeof _addCardsAttribute> | ReturnType<typeof editCardAttr> | ReturnType<typeof removeCardAttr> | ReturnType<typeof reorderCardsAttribute> | ReturnType<typeof reorderCharacterAttribute> | ReturnType<typeof reorderPlacesAttribute> | ReturnType<typeof _addNotesAttribute> | ReturnType<typeof editNoteAttr> | ReturnType<typeof removeNoteAttr> | ReturnType<typeof reorderNotesAttribute> | ReturnType<typeof load>} CustomAttributesActions
 */

/**
 * @param {String} name
 * @return {String}
 */
export const escapeBraces = (name) => {
  return name?.replace(/[{}]/g, '')
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @return {LegacyCustomAttribute}
 */
export const escapeBracesInLegacyAttributeName = (attribute) => {
  if (attribute) {
    return {
      ...attribute,
      name: escapeBraces(attribute.name),
    }
  } else {
    return attribute
  }
}

/**
 * @param {Attribute} attribute
 * @return {Attribute}
 */
export const escapeBracesInAttributeName = (attribute) => {
  if (attribute) {
    return {
      ...attribute,
      name: escapeBraces(attribute.name),
    }
  } else {
    return attribute
  }
}

/**
 * @param {String} message
 * @return {{ type: 'CUSTOM_ATTRIBUTE_ERROR', message: String }}
 */
export const _customAttributeError = (message) => {
  return {
    type: CUSTOM_ATTRIBUTE_ERROR,
    message,
  }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @return {{ type: 'ADD_PLACES_ATTRIBUTE', attribute: LegacyCustomAttribute }}
 */
export const _addPlacesAttribute = (attribute) => {
  return {
    type: ADD_PLACES_ATTRIBUTE,
    attribute,
  }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @return {function(Dispatch, GetState): Void}
 */
export function addPlaceAttr(attribute) {
  return function (dispatch) {
    const invalidAttributeName = Object.keys(initialState.place).find((key) => {
      return attribute?.name === key
    })
    if (
      typeof attribute?.name !== 'string' ||
      (attribute?.type !== 'text' && attribute?.type !== 'paragraph')
    ) {
      dispatch(_customAttributeError('Invalid attribute descriptor'))
    } else if (invalidAttributeName) {
      dispatch(_customAttributeError(`${invalidAttributeName} is not a valid place attribute name`))
    } else {
      dispatch(_addPlacesAttribute(escapeBracesInLegacyAttributeName(attribute)))
    }
  }
}

/**
 * @param {String} attribute
 * @return {{ type: 'REMOVE_PLACES_ATTRIBUTE', attribute: String }}
 */
export function removePlaceAttr(attribute) {
  return { type: REMOVE_PLACES_ATTRIBUTE, attribute }
}

/**
 * @param {number} index
 * @param {LegacyCustomAttribute} oldAttribute
 * @param {LegacyCustomAttribute} newAttribute
 * @return {{ type: 'EDIT_PLACES_ATTRIBUTE', index: number, oldAttribute: LegacyCustomAttribute, newAttribute: LegacyCustomAttribute }}
 */
export function editPlaceAttr(index, oldAttribute, newAttribute) {
  return {
    type: EDIT_PLACES_ATTRIBUTE,
    index,
    oldAttribute: escapeBracesInLegacyAttributeName(oldAttribute),
    newAttribute: escapeBracesInLegacyAttributeName(newAttribute),
  }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @return {{ type: 'ADD_LINES_ATTRIBUTE', attribute: LegacyCustomAttribute }}
 */
export function addLineAttr(attribute) {
  return { type: ADD_LINES_ATTRIBUTE, attribute: escapeBracesInLegacyAttributeName(attribute) }
}

/**
 * @param {String} attribute
 * @return {{ type: 'REMOVE_LINES_ATTRIBUTE', attribute: String }}
 */
export function removeLineAttr(attribute) {
  return { type: REMOVE_LINES_ATTRIBUTE, attribute }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @return {{ type: ADD_CARDS_ATTRIBUTE, attribute: LegacyCustomAttribute }}
 */
export const _addCardsAttribute = (attribute) => {
  return {
    type: ADD_CARDS_ATTRIBUTE,
    attribute,
  }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @return {function(Dispatch, GetState): Void}
 */
export function addCardAttr(attribute) {
  return function (dispatch) {
    const invalidAttributeName = Object.keys(initialState.card).find((key) => {
      return attribute?.name === key
    })
    if (
      typeof attribute?.name !== 'string' ||
      (attribute?.type !== 'text' && attribute?.type !== 'paragraph')
    ) {
      dispatch(_customAttributeError('Invalid attribute descriptor'))
    } else if (invalidAttributeName) {
      dispatch(_customAttributeError(`${invalidAttributeName} is not a valid place attribute name`))
    } else {
      dispatch(_addCardsAttribute(escapeBracesInLegacyAttributeName(attribute)))
    }
  }
}

/**
 * @param {number} index
 * @param {LegacyCustomAttribute} oldAttribute
 * @param {LegacyCustomAttribute} newAttribute
 * @return {{ type: 'EDIT_CARDS_ATTRIBUTE', index: number, oldAttribute: LegacyCustomAttribute, newAttribute: LegacyCustomAttribute }}
 */
export function editCardAttr(index, oldAttribute, newAttribute) {
  return {
    type: EDIT_CARDS_ATTRIBUTE,
    index,
    oldAttribute: escapeBracesInLegacyAttributeName(oldAttribute),
    newAttribute: escapeBracesInLegacyAttributeName(newAttribute),
  }
}

/**
 * @param {String} attribute
 * @return {{ type: 'REMOVE_CARDS_ATTRIBUTE', attribute: String }}
 */
export function removeCardAttr(attribute) {
  return { type: REMOVE_CARDS_ATTRIBUTE, attribute }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @param {number} toIndex
 * @return {{ type: 'REORDER_CARDS_ATTRIBUTE', attribute: LegacyCustomAttribute, toIndex: number }}
 */
export function reorderCardsAttribute(attribute, toIndex) {
  return {
    type: REORDER_CARDS_ATTRIBUTE,
    attribute: escapeBracesInLegacyAttributeName(attribute),
    toIndex,
  }
}

/**
 * @param {Attribute} attribute
 * @param {number} toIndex
 * @return {{ type: 'REORDER_CHARACTER_ATTRIBUTE', attribute: Attribute | LegacyCustomAttribute, toIndex: number }}
 */
export function reorderCharacterAttribute(attribute, toIndex) {
  return {
    type: REORDER_CHARACTER_ATTRIBUTE,
    attribute: escapeBracesInAttributeName(attribute),
    toIndex,
  }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @param {number} toIndex
 * @return {{ type: 'REORDER_PLACES_ATTRIBUTE', attribute: LegacyCustomAttribute, toIndex: number }}
 */
export function reorderPlacesAttribute(attribute, toIndex) {
  return {
    type: REORDER_PLACES_ATTRIBUTE,
    attribute: escapeBracesInLegacyAttributeName(attribute),
    toIndex,
  }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @return {{ type: 'ADD_NOTES_ATTRIBUTE', attribute: LegacyCustomAttribute }}
 */
export const _addNotesAttribute = (attribute) => {
  return {
    type: ADD_NOTES_ATTRIBUTE,
    attribute,
  }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @return {function(Dispatch, GetState): Void}
 */
export function addNoteAttr(attribute) {
  return function (dispatch) {
    const invalidAttributeName = Object.keys(initialState.note).find((key) => {
      return attribute?.name === key
    })
    if (
      typeof attribute?.name !== 'string' ||
      (attribute?.type !== 'text' && attribute?.type !== 'paragraph')
    ) {
      dispatch(_customAttributeError('Invalid attribute descriptor'))
    } else if (invalidAttributeName) {
      dispatch(_customAttributeError(`${invalidAttributeName} is not a valid place attribute name`))
    } else {
      dispatch(_addNotesAttribute(escapeBracesInLegacyAttributeName(attribute)))
    }
  }
}

/**
 * @param {number} index
 * @param {LegacyCustomAttribute} oldAttribute
 * @param {LegacyCustomAttribute} newAttribute
 * @return {{ type: 'EDIT_NOTES_ATTRIBUTE', index: number, oldAttribute: LegacyCustomAttribute, newAttribute: LegacyCustomAttribute }}
 */
export function editNoteAttr(index, oldAttribute, newAttribute) {
  return {
    type: EDIT_NOTES_ATTRIBUTE,
    index,
    oldAttribute: escapeBracesInLegacyAttributeName(oldAttribute),
    newAttribute: escapeBracesInLegacyAttributeName(newAttribute),
  }
}

/**
 * @param {String} attribute
 * @return {{ type: 'REMOVE_NOTES_ATTRIBUTE', attribute: String }}
 */
export function removeNoteAttr(attribute) {
  return { type: REMOVE_NOTES_ATTRIBUTE, attribute }
}

/**
 * @param {LegacyCustomAttribute} attribute
 * @param {number} toIndex
 * @return {{ type: 'REORDER_NOTES_ATTRIBUTE', attribute: LegacyCustomAttribute, toIndex: number }}
 */
export function reorderNotesAttribute(attribute, toIndex) {
  return {
    type: REORDER_NOTES_ATTRIBUTE,
    attribute: escapeBracesInLegacyAttributeName(attribute),
    toIndex,
  }
}

/**
 * @param {boolean} patching
 * @param {LegacyCustomAttributes} customAttributes
 * @return {{ type: 'LOAD_CUSTOM_ATTRIBUTES', patching: boolean, customAttributes: LegacyCustomAttributes }}
 */
export function load(patching, customAttributes) {
  return { type: LOAD_CUSTOM_ATTRIBUTES, patching, customAttributes }
}
